import { Lock } from "lucide-react";
import { hasCapability, type Capability } from "@headerhawk/core/plans";
import { cn } from "@headerhawk/ui";
import { BadgeTooltip } from "@/components/ui/badge-tooltip";
import { isDemoTab } from "@/lib/auth/impersonation";
import { useSubscription } from "@/lib/hooks/use-subscription";
import { describePlansWith, lowestPlanName } from "@/lib/plan-names";

interface PlanBadgeProps {
  /** The capability that gates the feature this badge sits on. */
  capability: Capability;
  className?: string;
}

/**
 * The least plan a gated feature needs, on the feature itself: a tab, a
 * card, a sidebar entry (task 450).
 *
 * Drawn where the plan in view lacks the feature, with a lock, and on every
 * gated feature in the public demo, whose organisation holds every plan's
 * features: the demo's banner used to say it per screen, in a second line
 * every visitor read. Nothing until the plan is known, so a paying tenant is
 * never shown an upsell while its subscription loads.
 */
export function PlanBadge({ capability, className }: PlanBadgeProps) {
  const { data: subscription } = useSubscription();
  const locked =
    subscription !== undefined && !hasCapability(subscription.plan, capability);
  if (!locked && !isDemoTab()) return null;
  return (
    <BadgeTooltip content={describePlansWith(capability)}>
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium leading-none text-muted-foreground",
          className,
        )}
        data-testid="plan-badge"
        data-capability={capability}
      >
        {locked && <Lock className="h-3 w-3" aria-hidden="true" />}
        {lowestPlanName(capability)}
      </span>
    </BadgeTooltip>
  );
}
